import { createStore } from 'zustand'
import { ScreenshotStore } from './types'
import { delay } from '@fable/utils'
import { toSvg, toWebp } from '../data/transformations'

export const createScreenshotStore = () => {
  return createStore<ScreenshotStore>()((set, get) => ({
    status: 'idle',
    image: '',
    svg: '',
    retry: 0,
    delay: 500,
    component: null,
    options: null,
    setStatus: (status) => set({ status }),
    createSvg: async () => {
      const { component, options, delay: delayTime } = get()

      if (!component) {
        set({ status: 'error' })
        return
      }

      set({ status: 'loading' })

      try {
        await delay(delayTime)

        const url = await toSvg(component, options || {})

        set({ svg: url })
      } catch (e) {
        console.error('Error creating screenshot SVG:', e)
        set({ status: 'error' })
      }
    },
    createImage: async () => {
      const { svg, retry } = get()
      if (!svg?.length) {
        set({ status: 'error' })
        return null
      }

      try {
        let retryCount = 0
        let image: string | undefined

        // NOTE: Sometimes the child images in the SVG do not load,
        // so here we can force multiple retries to correctly load the PNG
        for (retryCount = 0; retryCount < retry; retryCount++) {
          image = await toWebp(svg)
          if (!!image.length) {
            console.info(`Screenshot generation attempt ${retryCount + 1}.`)
          } else if (retryCount === retry - 1) {
            throw new Error('Screenshot could not be created')
          }
        }

        if (image) {
          set({ image, status: 'success' })
          return image
        } else {
          set({ status: 'error' })
          return null
        }
      } catch (e) {
        console.error('Error creating screenshot image:', e)
        set({ status: 'error' })
        return null
      }
    },

    takeScreenshot: async ({ component, options, retry, delay }) => {
      set({ component, options, retry, delay })

      const { createSvg, createImage } = get()

      await createSvg()
      return await createImage()
    },
    refresh: async ({ retry } = {}) => {
      set({ retry })

      const { createSvg, createImage } = get()

      await createSvg()
      return await createImage()
    },
    clear: () => set({ status: 'idle', image: '', svg: '', component: null }),
  }))
}
